/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState } from 'react';
import { useFormikContext, Field } from 'formik';
import { iNominationFormValues } from '@services/NominationService';
import { TileRadioAccessibleWithInfo, TextInput, Modal, Button, NominationAccordion } from '@components';
import { tile } from '@interfaces/interfaces';
import gridStyles from '@pages/forms/Grid.module.scss';
import styles from './NominationStep5.module.scss';

function NominationStep5(): ReactElement {
  const [loading, setLoading] = useState(false);
  const context = useFormikContext();
  const { values }: { values: iNominationFormValues } = useFormikContext();
  const vehicleContent0 = (
    <div>
      <p>Please provide the driver’s name, current address and date of birth or licence number. If you’re nominating a company, we will need the ACN/ABN number.</p>
      <p>If the offence involves a taxi, you cannot nominate another person if you're recorded as the driver in the operator's records.</p>
    </div>
  );
  const vehicleContent1 = (
    <div><p>Please provide proof of the sale/disposal of the vehicle/trailer, along with the buyer’s details.</p><p>If you have not yet notified <a href="https://www.vicroads.vic.gov.au/registration/buy-sell-or-transfer-a-vehicle" target="_blank" rel="noopener noreferrer">VicRoads</a> (link opens in new window) of the vehicle’s sale/disposal, you should do this quickly. If you do not transfer ownership of the vehicle/trailer, infringements may still be issued to you as the registered owner.</p></div>
  );
  const vehicleContent2 = (
    <p>A police report is recommended. If the theft of the vehicle/trailer or theft/copying of the number plates was reported to police, please provide the Victoria Police reference number or upload the police report.</p>
  );
  const vehicleContent3 = (
    <p>If you believe you have been nominated in error, please tell us why you are not responsible for the offence or supply a statement retracting the nomination from the person or company who nominated you.</p>
  );
  const vehicleContent4 = (
    <div><p>Under the <i>Road Safety Act 1986</i>, failure to keep records is not an adequate reason, unless under exceptional circumstances.</p><p>If the infringement involves a commercial passenger vehicle, evidence confirming the vehicle was not being used commercially at the time of offence is recommended.</p></div>
  );
  const marineContent0 = (
    <div><p>Please provide the operator's name, current address and date of birth or licence number. If you’re nominating a company, we will need the ACN/ABN number.</p></div>
  );
  const marineContent1 = (
    <div><p>Please provide proof of the sale/disposal of the vessel, along with the buyer’s details. </p><p>If you have not yet notified <a href="https://www.vicroads.vic.gov.au/registration/buy-sell-or-transfer-a-vehicle" target="_blank" rel="noopener noreferrer">VicRoads</a> (link opens in new window) of the vessel's sale/disposal, you should do this quickly. If you do not transfer ownership of the vessel, infringements may still be issued to you as the registered owner.</p></div>
  );
  const marineContent2 = (
    <p>If the theft of the vessel was reported to police, please provide the Victoria Police reference number.</p>
  );
  const marineContent3 = (
    <p>If you believe you have been nominated in error, please tell us why you are not responsible for the offence or supply a statement retracting the nomination from the person or company who nomiated you. </p>
  );
  const marineContent4 = (
    <div><p>Under the <i>Marine Safety Act 2010</i>, failure to keep records is not an adequate reason, unless under exceptional circumstances.</p></div>
  );
  const notMarineOffence: tile[] = [
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={vehicleContent0} itemOption="SelectedGroundInfo0" />,
      value: 'Someone else was in possession of the vehicle at the time of offence',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={vehicleContent1} itemOption="SelectedGroundInfo1" />,
      value: 'The vehicle was sold or permanently disposed of before the offence',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={vehicleContent2} itemOption="SelectedGroundInfo2" />,
      value: 'The vehicle or number plates were stolen or cloned/copied',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={vehicleContent3} itemOption="SelectedGroundInfo3" />,
      value: 'You were wrongly nominated as the person responsible for the offence',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={vehicleContent4} itemOption="SelectedGroundInfo4" />,
      value: 'You cannot identify who was in possession of the vehicle at the time of the offence',
    }
  ];
  const marineOffence: tile[] = [
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={marineContent0} itemOption="SelectedGroundInfo0" />,
      value: 'Someone else was the person in possession of the vessel at the time of offence',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={marineContent1} itemOption="SelectedGroundInfo1" />,
      value: 'The vessel was sold or permanently disposed of before the offence',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={marineContent2} itemOption="SelectedGroundInfo2" />,
      value: 'The vessel was stolen or the identification mark displayed on the vessel was false',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={marineContent3} itemOption="SelectedGroundInfo3" />,
      value: 'You were wrongly nominated as the person responsible for the offence',
    },
    {
      body: <NominationAccordion selectOptionName="selectTheOption" closeOptionName="closeTheOption" bodyContent={marineContent4} itemOption="SelectedGroundInfo4" />,
      value: 'You cannot identify who was in possession of the vessel at the time of the offence',
    }
  ];
  const filteredTileData = values.IsMarineOffence ? marineOffence : notMarineOffence;

  useEffect(() => {
    if (values.selectTheOption) {
      context.setFieldTouched('selectTheOption', true);
    }
  }, []);
  return (
    <>
      {!loading && (
        <>
          <div className="vfH2">Please select the option that applies</div>
          <p>You may only apply under one of the nomination statements below.</p>
          <div>
            <div className={gridStyles.gridColFull}>
              <div className={styles.col}>
                <Field
                  name="SelectedGroundInfo"
                  component={TileRadioAccessibleWithInfo}
                  tileData={filteredTileData}
                  orientationColumn
                  hasBeenNominatedBefore={values.HasBeenNominatedBefore}
                  validateFieldName="selectTheOption" />
                <br /><br />
                <p>If you are applying for reasons of family violence, it may be more appropriate to lodge a <a href="/fvs-form" target="_blank" rel="noopener noreferrer">Family Violence Scheme application</a>. The Family Violence Scheme helps victim-survivors if there is a link between your experience of family violence and the fines.</p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default NominationStep5;
